import React, { useState, useEffect, useMemo } from 'react';
import { BookOpen, RefreshCw, RotateCcw, X, FileText } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { confirmDialog } from '../lib/confirm';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const RUBRIEKEN = ['Activa', 'Passiva', 'Opbrengsten', 'Kosten'];
const debetNatuur = (rubriek) => rubriek === 'Activa' || rubriek === 'Kosten';

export default function Grootboek() {
  const { t } = useT();
  const [rekeningen, setRekeningen] = useState([]);
  const [posten, setPosten] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null); // rekening
  const [showJournaal, setShowJournaal] = useState(false);

  const load = () => Promise.all([carsysApi.getGrootboekRekeningen(), carsysApi.getJournaalposten()])
    .then(([r, j]) => { setRekeningen(r); setPosten(j); })
    .catch(() => {});

  useEffect(() => { load(); }, []);

  // Saldo per rekeningcode
  const saldi = useMemo(() => {
    const m = {};
    rekeningen.forEach(r => { m[r.code] = { ...r, debet: 0, credit: 0 }; });
    posten.forEach(j => (j.regels || []).forEach(l => {
      if (!m[l.rekening]) m[l.rekening] = { code: l.rekening, naam: l.rekening, rubriek: 'Overig', debet: 0, credit: 0 };
      m[l.rekening].debet += (Number(l.debet) || 0);
      m[l.rekening].credit += (Number(l.credit) || 0);
    }));
    Object.values(m).forEach(a => { a.saldo = debetNatuur(a.rubriek) ? a.debet - a.credit : a.credit - a.debet; });
    return m;
  }, [rekeningen, posten]);

  const genereer = async () => {
    setBusy(true);
    try {
      const res = await carsysApi.genereerBoekhouding();
      toast.success(res.created > 0 ? `${res.created} ${t('nieuwe journaalposten aangemaakt', 'nieuwe journaalposten aangemaakt')}` : t('Boekhouding is al up-to-date', 'Boekhouding is al up-to-date'));
      await load();
    } catch (e) { toast.error(t('Genereren mislukt', 'Genereren mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const opnieuw = async () => {
    if (!(await confirmDialog(t('Alle automatische journaalposten verwijderen en opnieuw opbouwen?', 'Alle automatische journaalposten verwijderen en opnieuw opbouwen?')))) return;
    setBusy(true);
    try {
      await carsysApi.resetBoekhouding();
      const res = await carsysApi.genereerBoekhouding();
      toast.success(`${t('Opnieuw opgebouwd', 'Opnieuw opgebouwd')}: ${res.created} ${t('posten', 'posten')}`);
      await load();
    } catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const mutaties = (code) => posten
    .filter(j => (j.regels || []).some(l => l.rekening === code))
    .map(j => { const l = (j.regels || []).find(x => x.rekening === code); return { ...j, debet: l?.debet || 0, credit: l?.credit || 0 }; })
    .sort((a, b) => (a.datum || '').localeCompare(b.datum || ''));

  const totaalDebet = posten.reduce((s, j) => s + (j.regels || []).reduce((x, l) => x + (Number(l.debet) || 0), 0), 0);
  const totaalCredit = posten.reduce((s, j) => s + (j.regels || []).reduce((x, l) => x + (Number(l.credit) || 0), 0), 0);

  return (
    <div data-testid="grootboek-page">
      <div className="cs-page-header">
        <div><h2>{t('Grootboek', 'Grootboek')}</h2><p>{t('Dubbel boekhouden — saldi per grootboekrekening, automatisch geboekt uit je facturen, betalingen en kosten', 'Dubbel boekhouden — saldi per grootboekrekening, automatisch geboekt uit je facturen, betalingen en kosten')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn" data-testid="btn-journaal-toggle" onClick={() => setShowJournaal(s => !s)}><FileText size={14} /> {showJournaal ? t('Verberg journaal', 'Verberg journaal') : t('Toon journaal', 'Toon journaal')} ({posten.length})</button>
          <button className="cs-btn" data-testid="btn-grootboek-reset" disabled={busy} onClick={opnieuw}><RotateCcw size={14} /> {t('Opnieuw opbouwen', 'Opnieuw opbouwen')}</button>
          <button className="cs-btn primary" data-testid="btn-grootboek-genereer" disabled={busy} onClick={genereer}><RefreshCw size={14} /> {busy ? t('Bezig...', 'Bezig...') : t('Bijwerken', 'Bijwerken')}</button>
        </div>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi" data-testid="kpi-gb-posten"><div className="label">{t('Journaalposten', 'Journaalposten')}</div><div className="value">{posten.length}</div></div>
        <div className="cs-kpi"><div className="label">{t('Totaal debet', 'Totaal debet')}</div><div className="value">{eur(totaalDebet)}</div></div>
        <div className="cs-kpi" data-testid="kpi-gb-balans"><div className="label">{t('Totaal credit', 'Totaal credit')}</div><div className="value" style={{ color: Math.abs(totaalDebet - totaalCredit) < 0.02 ? 'var(--cs-green)' : 'var(--cs-red)' }}>{eur(totaalCredit)}</div></div>
      </div>

      {showJournaal ? (
        <div className="cs-card">
          <table className="cs-table">
            <thead><tr><th>{t('Datum', 'Datum')}</th><th>{t('Omschrijving', 'Omschrijving')}</th><th>{t('Boekingen', 'Boekingen')}</th><th style={{ textAlign: 'right' }}>{t('Bedrag', 'Bedrag')}</th></tr></thead>
            <tbody>
              {posten.map(j => {
                const bedrag = (j.regels || []).reduce((s, l) => s + (Number(l.debet) || 0), 0);
                return (
                  <tr key={j.id} data-testid={`journaal-row-${j.id}`}>
                    <td>{j.datum}</td>
                    <td style={{ fontWeight: 600 }}>{j.omschrijving}</td>
                    <td style={{ fontSize: 12, color: 'var(--cs-text-muted)' }}>
                      {(j.regels || []).map((l, i) => (
                        <span key={i}>{saldi[l.rekening]?.naam || l.rekening} {l.debet > 0 ? `D ${eur(l.debet)}` : `C ${eur(l.credit)}`}{i < j.regels.length - 1 ? ' · ' : ''}</span>
                      ))}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(bedrag)}</td>
                  </tr>
                );
              })}
              {posten.length === 0 && <tr><td colSpan={4} className="cs-empty">{t('Nog geen journaalposten. Klik op "Bijwerken" om automatisch te boeken uit je facturen, betalingen en kosten.', 'Nog geen journaalposten. Klik op "Bijwerken" om automatisch te boeken uit je facturen, betalingen en kosten.')}</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        RUBRIEKEN.map(rub => {
          const items = Object.values(saldi).filter(a => a.rubriek === rub && (a.debet !== 0 || a.credit !== 0));
          if (items.length === 0) return null;
          const totSaldo = items.reduce((s, a) => s + a.saldo, 0);
          return (
            <div className="cs-card" key={rub} style={{ marginBottom: 14 }}>
              <h3 className="cs-card-title" style={{ padding: '12px 14px 0' }}><BookOpen size={16} /> {t(rub, rub)}</h3>
              <table className="cs-table">
                <thead><tr><th style={{ width: 70 }}>{t('Code', 'Code')}</th><th>{t('Rekening', 'Rekening')}</th><th style={{ textAlign: 'right' }}>{t('Debet', 'Debet')}</th><th style={{ textAlign: 'right' }}>{t('Credit', 'Credit')}</th><th style={{ textAlign: 'right' }}>{t('Saldo', 'Saldo')}</th></tr></thead>
                <tbody>
                  {items.sort((a, b) => a.code.localeCompare(b.code)).map(a => (
                    <tr key={a.code} data-testid={`gb-rekening-${a.code}`} style={{ cursor: 'pointer' }} onClick={() => setDetail(a)} title={t('Klik voor mutaties', 'Klik voor mutaties')}>
                      <td style={{ fontFamily: 'ui-monospace, monospace' }}>{a.code}</td>
                      <td>{a.naam}</td>
                      <td style={{ textAlign: 'right', color: 'var(--cs-text-muted)' }}>{a.debet ? eur(a.debet) : '-'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--cs-text-muted)' }}>{a.credit ? eur(a.credit) : '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(a.saldo)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid var(--cs-border)', fontWeight: 700 }}>
                    <td></td><td>{t('Totaal', 'Totaal')} {t(rub, rub)}</td><td></td><td></td>
                    <td style={{ textAlign: 'right' }}>{eur(totSaldo)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })
      )}

      {detail && (
        <div className="cs-modal-overlay" onClick={() => setDetail(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ width: 640 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <h3 style={{ marginTop: 0 }}>{detail.code} · {detail.naam}</h3>
              <button className="cs-btn ghost" style={{ padding: 4 }} onClick={() => setDetail(null)}><X size={16} /></button>
            </div>
            <table className="cs-table">
              <thead><tr><th>{t('Datum', 'Datum')}</th><th>{t('Omschrijving', 'Omschrijving')}</th><th style={{ textAlign: 'right' }}>{t('Debet', 'Debet')}</th><th style={{ textAlign: 'right' }}>{t('Credit', 'Credit')}</th></tr></thead>
              <tbody>
                {mutaties(detail.code).map(m => (
                  <tr key={m.id}>
                    <td>{m.datum}</td><td>{m.omschrijving}</td>
                    <td style={{ textAlign: 'right' }}>{m.debet ? eur(m.debet) : '-'}</td>
                    <td style={{ textAlign: 'right' }}>{m.credit ? eur(m.credit) : '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr style={{ fontWeight: 700, borderTop: '2px solid var(--cs-border)' }}>
                <td colSpan={2}>{t('Saldo', 'Saldo')}</td>
                <td colSpan={2} style={{ textAlign: 'right' }}>{eur(detail.saldo)}</td>
              </tr></tfoot>
            </table>
          </div>
        </div>
      )}

      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; max-width: 92vw; box-shadow: 0 20px 60px rgba(0,0,0,0.25); max-height: 88vh; overflow: auto; }
      `}</style>
    </div>
  );
}
